import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Switch,
  SwitchProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { updateData } from 'services/CommonServices';
import { ImpersonateTypes } from 'types';
import { SETUP_ENDPOINT } from 'utils/Constant';
import { alertOptions } from 'utils/helper';
import Storage from 'utils/Storage';
import { ImpersonateSchema } from 'utils/ValidatorSchema';

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  '& .MuiButtonBase-root': {
    padding: '0 0 !important',
  },
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        border: 0,
        opacity: 1,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
      color: '#fff',
      transform: 'translateX(16px)',
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      border: '6px solid #fff',
      color: '#33cf4d',
    },
    margin: 2,
    padding: 0,
    transitionDuration: '300ms',
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    height: 22,
    width: 22,
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    borderRadius: 26 / 2,
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
  height: 26,
  padding: 0,
  width: 42,
}));

type Props = {
  enable: boolean;
};
export const Impersonate: React.FC<Props> = (props: Props) => {
  const { enable } = props;
  const [edit, setEdit] = React.useState(false);
  const [loaderPG, setLoaderPG] = React.useState(false);
  const TokenStatus = Storage.getTokens();
  const cancelFormPg = () => {
    setEdit(false);
  };
  const {
    control: controlPG,
    handleSubmit: handlePGSubmit,
    setValue,
  } = useForm<ImpersonateTypes>({
    criteriaMode: 'all',
    defaultValues: {
      show: false,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(ImpersonateSchema),
  });

  const onSubmitPGForm = (data: ImpersonateTypes) => {
    setLoaderPG(true);
    updateData('', { ...data }, SETUP_ENDPOINT.AdminSupportImpersonate)
      .then(() => {
        setLoaderPG(false);
        setEdit(false);
        toast.success(
          `Admin support ${
            data.show
              ? 'has been enabled. Visas.AI team can now access your firm to provide support.'
              : 'has been disabled. Visas.AI will no longer have access to your firm.'
          }`,
          alertOptions,
        );
      })
      .catch(() => {
        setLoaderPG(false);
      });
  };
  React.useEffect(() => {
    if (enable) {
      setValue('show', true);
    } else {
      setValue('show', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enable]);
  return (
    <fieldset
      style={{ border: 'none', padding: 'unset' }}
      hidden={TokenStatus?.is_logged_in_from_admin}>
      <form id="setting-enable-disable">
        <Typography component={'div'} className="addbox">
          <Typography component={'div'} className="d-flex-a">
            <Typography
              component={'div'}
              className="uadd-title"
              sx={{ paddingTop: '0.5rem !important' }}>
              Admin Support
            </Typography>
            {!edit ? (
              <Typography
                component={'div'}
                className="d-flex-jae"
                sx={{ cursor: 'pointer', paddingLeft: '5px' }}
                onClick={() => setEdit(true)}>
                <Tooltip title={'Edit'} placement={'top-end'}>
                  <EditIcon sx={{ fill: '#275fce' }} />
                </Tooltip>
              </Typography>
            ) : null}
          </Typography>
          <Box
            component="form"
            className="u-add-in"
            noValidate
            autoComplete="off">
            <Grid container columnSpacing={{ md: 3, sm: 1 }}>
              <Grid item md={12}>
                <Typography component={'div'} className="custom-field d-flex-a">
                  <Typography
                    component={'div'}
                    className="custom-label"
                    sx={{
                      marginRight: '16px !important',
                      marginTop: '10px !important',
                    }}>
                    Do you wish to allow Visas.AI Admin to access and support
                    your firm?
                  </Typography>

                  <Controller
                    control={controlPG}
                    defaultValue={false}
                    name="show"
                    render={({ field: { onChange, value, name, ref } }) => {
                      return (
                        <FormGroup sx={{ marginLeft: '30px' }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center">
                            <Typography
                              className="custom-label"
                              sx={{
                                marginTop: '10px !important',
                              }}>
                              No
                            </Typography>
                            <FormControlLabel
                              disabled={!edit || loaderPG}
                              control={
                                <IOSSwitch
                                  sx={{ marginLeft: '2px' }}
                                  checked={value}
                                  onChange={(e) => {
                                    onChange(e);
                                    handlePGSubmit(onSubmitPGForm)();
                                  }}
                                  disabled={!edit || loaderPG}
                                />
                              }
                              label={''}
                            />
                            <Typography
                              className="custom-label"
                              sx={{
                                marginTop: '10px !important',
                              }}>
                              Yes
                            </Typography>
                          </Stack>
                        </FormGroup>
                      );
                    }}
                  />
                </Typography>
              </Grid>
              {edit ? (
                <Typography component={'div'} className="d-flex-jae w-100">
                  <Button
                    className="cancel-btn mr-2"
                    onClick={() => {
                      cancelFormPg();
                    }}>
                    <Typography className="o-box" />
                    Cancel
                  </Button>
                </Typography>
              ) : null}
            </Grid>
          </Box>
        </Typography>
      </form>
    </fieldset>
  );
};
